import Typography from '@mui/material/Typography';
import {formatCopy} from 'utils/typography';
import React from "react";
import { useTranslation } from "react-i18next";
import {useFormContext, Controller} from "react-hook-form";

import TextField from "@mui/material/TextField";

import { PACKAGE_FORM, PackageForm } from '../add_form/helpers';

import {checkDecimalsAfterDot} from '../../../../../../../../../../../../../utils/validation'
import {ADORNMENTS} from "../../../../../../../../../../../../../config/constants";
import {useFormattedDecimalInput} from '../../../../../../../../../../../../../utils/hooks';
import {useDemoPackagingPlaybackComplete} from 'components/cabinet/demo/steps/packaging/use-packaging';
import {LinkPopover} from '@vedmarket/ui-kit';

const CommonPackage = () => {

  const { t } = useTranslation()

  const { getValues, trigger, control } = useFormContext<PackageForm>()

  const isDemoPlaybackComplete = useDemoPackagingPlaybackComplete()

  const decimalInput = useFormattedDecimalInput();

  const errorValue = t('errors__message__incorrectValueTwoDecimalsAfterComma');
  const errorValueThreeSymbolsAfterDot = t('errors__message__incorrectValueThreeDecimalsAfterComma');

  const checkGrossWeightValueIsCorrect = (value: number) => {
    const netValue = getValues()[PACKAGE_FORM.NET_WEIGHT_MANUAL]

    const hasTwoDecimalsError = checkDecimalsAfterDot(value, errorValue);

    if (hasTwoDecimalsError !== true) {
      return hasTwoDecimalsError
    }

    return Number(value) > Number(netValue) ? true: t('errors__message__incorrectValueGrossWeight');

  }

  return <>
    <div className='add-product__row'>
      <Controller
        name={PACKAGE_FORM.VOLUME_MANUAL}
        control={control}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorValueThreeSymbolsAfterDot, 3),
          min: { value: 0, message: errorValueThreeSymbolsAfterDot },
        }}
        render={({ field, fieldState }) => (
          <TextField
            className={`add-product__${PACKAGE_FORM.VOLUME_MANUAL}`}
            value={decimalInput.getDisplayValue({
              fieldName: PACKAGE_FORM.VOLUME_MANUAL,
              rawValue: field.value,
              decimals: 3,
            })}
            onChange={(e) => {
              decimalInput.startEditing(PACKAGE_FORM.VOLUME_MANUAL);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(PACKAGE_FORM.VOLUME_MANUAL);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={t('common__text__totalVolume')}
            InputLabelProps={{ shrink: true }}
            disabled={isDemoPlaybackComplete}
            InputProps={{ endAdornment: ADORNMENTS.VOLUME }}
          />
        )}
      />
      <Controller
        name={PACKAGE_FORM.NET_WEIGHT_MANUAL}
        control={control}
        rules={{
          required: true,
          validate: (value) => checkDecimalsAfterDot(value, errorValue),
          min: { value: 0, message: errorValue },
        }}
        render={({ field, fieldState }) => (
          <TextField
            className={`add-product__${PACKAGE_FORM.NET_WEIGHT_MANUAL}`}
            value={decimalInput.getDisplayValue({
              fieldName: PACKAGE_FORM.NET_WEIGHT_MANUAL,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(PACKAGE_FORM.NET_WEIGHT_MANUAL);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={async () => {
              field.onBlur();
              decimalInput.stopEditing(PACKAGE_FORM.NET_WEIGHT_MANUAL);
              if (getValues(PACKAGE_FORM.GROSS_WEIGHT_MANUAL) && getValues(PACKAGE_FORM.NET_WEIGHT_MANUAL)) {
                await trigger([ PACKAGE_FORM.GROSS_WEIGHT_MANUAL ])
              }
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={t('common__text__totalNetWeight')}
            InputLabelProps={{ shrink: true }}
            disabled={isDemoPlaybackComplete}
            InputProps={{ endAdornment: ADORNMENTS.WEIGHT }}
          />
        )}
      />
      <Controller
        name={PACKAGE_FORM.GROSS_WEIGHT_MANUAL}
        control={control}
        rules={{
          required: true,
          validate: (value: number) => checkGrossWeightValueIsCorrect(value),
          min: { value: 0, message: errorValue },
        }}
        render={({ field, fieldState }) => (
          <TextField
            className={`add-product__${PACKAGE_FORM.GROSS_WEIGHT_MANUAL}`}
            value={decimalInput.getDisplayValue({
              fieldName: PACKAGE_FORM.GROSS_WEIGHT_MANUAL,
              rawValue: field.value,
            })}
            onChange={(e) => {
              decimalInput.startEditing(PACKAGE_FORM.GROSS_WEIGHT_MANUAL);
              const value = e.target.value;
              const valueWithDot = value.replace(',', '.');
              field.onChange(valueWithDot);
            }}
            onBlur={() => {
              field.onBlur();
              decimalInput.stopEditing(PACKAGE_FORM.GROSS_WEIGHT_MANUAL);
            }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            variant='outlined'
            label={t('common__text__totalGrossWeight')}
            InputLabelProps={{ shrink: true }}
            disabled={isDemoPlaybackComplete}
            InputProps={{ endAdornment: ADORNMENTS.WEIGHT }}
          />
        )}
      />
      <div className='product-fields-help'>
        <LinkPopover
          linkText={t('calc__help__cargoParametersLink')}
          text={formatCopy(t('calc__help__cargoParameters'))}
        />
      </div>
    </div>
  </>
}

export default CommonPackage
